import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'next-i18next';
import type { FormSettings, UseMutateOnFailure } from '@mwl/core-lib';
import {
  applyPromocode,
  applyPromocodeDryRun,
  maxLengthValidator,
  minLengthValidator,
  requiredStringValidator,
  useForm,
  useSubmitForm,
} from '@mwl/core-lib';

import type { PromoFieldFormValues, PromoFieldMode } from './PromoFieldForm.types';

interface UseFormSettingsOptions {
  minLength?: number;
  maxLength?: number;
}

export const useFormSettings = ({ minLength = 1, maxLength = 16 }: UseFormSettingsOptions) => {
  const searchParams = useSearchParams();
  const promocode = searchParams.get('promocode');

  const { t } = useTranslation('promo');

  return useMemo<FormSettings<PromoFieldFormValues>>(
    () => ({
      promo: {
        value: promocode || '',
        rules: {
          required: {
            validate: requiredStringValidator,
            message: t('voucher_error_required', 'Promo code is required'),
          },
          minLength: {
            validate: minLengthValidator(minLength),
            message: t('voucher_error_min_length', 'Promo code must be at least 1 character long'),
          },
          maxLength: {
            validate: maxLengthValidator(maxLength),
            message: t('voucher_error_max_length', 'Promo code must be less than 16 characters'),
          },
        },
      },
    }),
    [t, promocode, maxLength, minLength],
  );
};

interface UsePromoFieldOptions {
  currency?: string;
  mode: PromoFieldMode;
  onError?: UseMutateOnFailure;
  formSettings: FormSettings<PromoFieldFormValues>;
  onSuccess?: () => void;
  onBeforeSubmit?: (values: PromoFieldFormValues) => void;
}

export const usePromoField = ({
  mode,
  currency,
  formSettings,
  onBeforeSubmit,
  onError,
  onSuccess,
}: UsePromoFieldOptions) => {
  const form = useForm<PromoFieldFormValues>(formSettings, { mode: 'onChange' });

  const requestHandler: Record<PromoFieldMode, typeof applyPromocode | typeof applyPromocodeDryRun> = {
    apply: applyPromocode,
    check: applyPromocodeDryRun,
  };

  const {
    state: mutationState,
    onResetData,
    onResetErrors,
    onResetSuccess,
    onSubmitConfirm,
  } = useSubmitForm({
    formState: form.formState,
    requestHandler: requestHandler[mode],
    onError,
    onSuccess,
  });

  const doOnSubmit = useCallback(
    (values: PromoFieldFormValues) => {
      onBeforeSubmit?.(values);
      onSubmitConfirm({ promocode: values.promo, currency });
    },
    [onSubmitConfirm, onBeforeSubmit, currency],
  );

  return {
    form,
    mutationState,
    doOnSubmit,
    resetSuccess: onResetSuccess,
    resetData: onResetData,
    resetErrors: onResetErrors,
  };
};
